import { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '../lib/supabase';
import type { ExtractedAction, MeetingReport } from '../types/meetingReport';
import type { Database, Json } from '../types/supabase';

type MeetingReportRow = Database['public']['Tables']['crm_meeting_reports']['Row'];

/** Coerce a JSON array from the DB into ExtractedAction[] (defaults to [] for null/non-array). */
function toExtractedActions(value: Json | null): ExtractedAction[] {
  return Array.isArray(value) ? (value as unknown as ExtractedAction[]) : [];
}

/** Map a crm_meeting_reports row onto the MeetingReport domain type. */
function toMeetingReport(row: MeetingReportRow): MeetingReport {
  return {
    id: row.id,
    appointment_id: row.appointment_id,
    note_id: row.note_id,
    google_meet_conference_id: row.google_meet_conference_id,
    transcript_raw: row.transcript_raw,
    transcript_fetched_at: row.transcript_fetched_at,
    cr_title: row.cr_title,
    cr_html: row.cr_html,
    cr_markdown: row.cr_markdown,
    cr_generated_at: row.cr_generated_at,
    prompt_template_used: row.prompt_template_used,
    extracted_actions: toExtractedActions(row.extracted_actions),
    validated_actions: toExtractedActions(row.validated_actions),
    actions_validated_at: row.actions_validated_at,
    email_sent_to: row.email_sent_to,
    email_sent_at: row.email_sent_at,
    email_resend_id: row.email_resend_id,
    status: row.status as MeetingReport['status'],
    error_message: row.error_message,
    owner_id: row.owner_id ?? '',
    created_at: row.created_at ?? '',
    updated_at: row.updated_at ?? '',
  };
}

/**
 * Hook pour récupérer les comptes-rendus de réunion liés à une entité.
 * Peut chercher par note_id, appointment_id, ou account_id (via appointments).
 */
export function useMeetingReports(options: {
  noteId?: string | null;
  appointmentId?: string | null;
  accountId?: string | null;
  enabled?: boolean;
}) {
  const { noteId, appointmentId, accountId, enabled = true } = options;
  const [reports, setReports] = useState<MeetingReport[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchReports = useCallback(async () => {
    if (!enabled) return;

    // Au moins un ID doit être fourni
    if (!noteId && !appointmentId && !accountId) {
      setReports([]);
      return;
    }

    setLoading(true);
    try {
      if (noteId || appointmentId) {
        // Chercher par note_id et/ou appointment_id (OR)
        const conditions: string[] = [];
        if (noteId) conditions.push(`note_id.eq.${noteId}`);
        if (appointmentId) conditions.push(`appointment_id.eq.${appointmentId}`);

        const { data } = await supabase
          .from('crm_meeting_reports')
          .select('*')
          .or(conditions.join(','))
          .order('created_at', { ascending: false });
        setReports((data || []).map(toMeetingReport));
      } else if (accountId) {
        // Chercher via les appointments ET les notes du compte
        const [appointmentsRes, notesRes] = await Promise.all([
          supabase.from('appointments').select('id').eq('account_id', accountId),
          supabase.from('crm_notes').select('id').eq('account_id', accountId),
        ]);

        const aptIds = (appointmentsRes.data || []).map((a) => a.id);
        const noteIds = (notesRes.data || []).map((n) => n.id);

        if (aptIds.length === 0 && noteIds.length === 0) {
          setReports([]);
        } else {
          // Construire une requête OR : appointment_id in aptIds OR note_id in noteIds
          const conditions: string[] = [];
          if (aptIds.length > 0) {
            conditions.push(`appointment_id.in.(${aptIds.join(',')})`);
          }
          if (noteIds.length > 0) {
            conditions.push(`note_id.in.(${noteIds.join(',')})`);
          }

          const { data } = await supabase
            .from('crm_meeting_reports')
            .select('*')
            .or(conditions.join(','))
            .order('created_at', { ascending: false });
          setReports((data || []).map(toMeetingReport));
        }
      }
    } catch (error) {
      console.error('Error fetching meeting reports:', error);
      setReports([]);
    } finally {
      setLoading(false);
    }
  }, [noteId, appointmentId, accountId, enabled]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  return { reports, loading, refetch: fetchReports };
}

/**
 * Hook simplifié : vérifie si un CR existe pour une note ou un RDV donné.
 * Retourne le premier report trouvé (le plus récent).
 */
export function useMeetingReport(options: {
  noteId?: string | null;
  appointmentId?: string | null;
  enabled?: boolean;
}) {
  const { reports, loading } = useMeetingReports({
    noteId: options.noteId,
    appointmentId: options.appointmentId,
    enabled: options.enabled,
  });
  return {
    report: reports[0] || null,
    hasReport: reports.length > 0,
    loading,
  };
}

/**
 * Batch-fetch meeting reports for multiple notes/appointments in a single query.
 * Returns lookup maps for O(1) access by noteId or appointmentId.
 */
export function useBatchMeetingReports(
  noteIds: string[],
  appointmentIds: string[],
) {
  const [reports, setReports] = useState<MeetingReport[]>([]);
  const [loading, setLoading] = useState(false);

  const noteIdsKey = noteIds.join(',');
  const appointmentIdsKey = appointmentIds.join(',');

  useEffect(() => {
    if (noteIds.length === 0 && appointmentIds.length === 0) {
      setReports([]);
      return;
    }

    const conditions: string[] = [];
    if (noteIds.length > 0) conditions.push(`note_id.in.(${noteIds.join(',')})`);
    if (appointmentIds.length > 0) conditions.push(`appointment_id.in.(${appointmentIds.join(',')})`);

    setLoading(true);
    supabase
      .from('crm_meeting_reports')
      .select('*')
      .or(conditions.join(','))
      .order('created_at', { ascending: false })
      .then(
        ({ data }) => {
          setReports((data || []).map(toMeetingReport));
          setLoading(false);
        },
        () => {
          setReports([]);
          setLoading(false);
        },
      );
  }, [noteIdsKey, appointmentIdsKey]);

  const byNote = useMemo(() => {
    const map = new Map<string, MeetingReport>();
    for (const r of reports) {
      if (r.note_id && !map.has(r.note_id)) map.set(r.note_id, r);
    }
    return map;
  }, [reports]);

  const byAppointment = useMemo(() => {
    const map = new Map<string, MeetingReport>();
    for (const r of reports) {
      if (r.appointment_id && !map.has(r.appointment_id)) map.set(r.appointment_id, r);
    }
    return map;
  }, [reports]);

  const findReport = useCallback(
    (noteId?: string | null, appointmentId?: string | null) => {
      if (noteId && byNote.has(noteId)) return byNote.get(noteId)!;
      if (appointmentId && byAppointment.has(appointmentId)) return byAppointment.get(appointmentId)!;
      return null;
    },
    [byNote, byAppointment],
  );

  return { reports, loading, byNote, byAppointment, findReport };
}
